import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BRAND, Logo, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';

type Stmt = { statement_number: string | null; property_id: string | null; period_start: string; period_end: string; currency: string; status: string; interventions_count: number; billable_costs_total: number; invoiced_total: number; paid_total: number; balance: number };
type Line = { id: string; section: string; line_date: string | null; description: string | null; amount: number };

export function StatementPrint() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [st, setSt] = useState<Stmt | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [propName, setPropName] = useState('—');

  useEffect(() => {
    (async () => {
      const s = (await supabase.from('owner_statements').select('*').eq('id', id).single()).data as any;
      setSt(s);
      const l = (await supabase.from('owner_statement_lines').select('id,section,line_date,description,amount').eq('statement_id', id).is('deleted_at', null).order('line_date')).data as any;
      setLines(l ?? []);
      if (s?.property_id) {
        const pl = (await supabase.from('v_pnl_lines').select('property_id,property_name').eq('property_id', s.property_id).limit(1)).data as any;
        if (pl?.[0]?.property_name) setPropName(pl[0].property_name);
      }
    })();
  }, [id]);
  useEffect(() => { if (st) { const x = setTimeout(() => window.print(), 400); return () => clearTimeout(x); } }, [st]);

  const C = BRAND.colors;
  if (!st) return <div className="p-10 text-muted">…</div>;
  const money = (n: number) => formatCurrency(n, st.currency, i18n.language);
  const sections = ['intervention', 'billable_cost', 'invoice', 'payment'];

  return (
    <div className="mx-auto max-w-[800px] bg-white p-10 text-[#0B0F17]" style={{ fontFamily: 'Geist, system-ui, sans-serif' }}>
      <div className="flex items-start justify-between border-b-2 pb-5" style={{ borderColor: C.ink }}>
        <Logo variant="light" size={34} />
        <div className="text-right"><div className="text-[20px] font-semibold">{t('nav.statements')}</div><div className="font-mono text-sm text-[#667085]">{st.statement_number ?? t('stm.draft')}</div></div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
        <div><div className="text-xs text-[#667085]">{t('nav.properties')}</div><div className="font-medium">{propName}</div></div>
        <div><div className="text-xs text-[#667085]">{t('stm.period')}</div><div className="font-mono">{st.period_start} – {st.period_end}</div></div>
      </div>

      {sections.map((sec) => {
        const ls = lines.filter((l) => l.section === sec);
        if (!ls.length) return null;
        return (
          <div key={sec} className="mt-6">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#667085]">{t('stm.sec_' + sec)}</div>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {ls.map((l) => (
                  <tr key={l.id} className="border-b" style={{ borderColor: '#E5E7EB' }}>
                    <td className="py-1.5 text-[#667085]">{l.line_date}</td>
                    <td className="py-1.5">{l.description}</td>
                    <td className="py-1.5 text-right font-mono">{sec === 'intervention' ? '' : money(Number(l.amount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      <div className="mt-8 ml-auto w-[280px] text-sm">
        <div className="flex justify-between py-1"><span className="text-[#667085]">{t('stm.billableCosts')}</span><span className="font-mono">{money(st.billable_costs_total)}</span></div>
        <div className="flex justify-between py-1"><span className="text-[#667085]">{t('stm.invoiced')}</span><span className="font-mono">{money(st.invoiced_total)}</span></div>
        <div className="flex justify-between py-1"><span className="text-[#667085]">{t('stm.paid')}</span><span className="font-mono">{money(st.paid_total)}</span></div>
        <div className="mt-1 flex justify-between border-t-2 py-1.5 font-semibold" style={{ borderColor: C.ink }}><span>{t('stm.balance')}</span><span className="font-mono">{money(st.balance)}</span></div>
      </div>
      <div className="mt-10 text-center text-xs text-[#98A2B3]">{BRAND.name}</div>
    </div>
  );
}
